import { Action, Reducer } from "redux";
import { ancillaryProductUserSelections, clearDealerChanges, getDealSuccess } from "src/store/actions/deal";
import { pollingSuccess } from "src/store/actions/poller";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { DealerChange } from "Types/api/DealerChange";
import { AncillaryProduct } from "Types/api/AncillaryProduct";

export interface DealerChangesState {
    changed: string[];
    added: string[];
    ciqUserChanges: {
        ancillaryProducts?: ActionType<typeof ancillaryProductUserSelections>["payload"];
    };
}

const initialState = {
    changed: [],
    added: [],
    ciqUserChanges: {},
};

type UpdateActions = ActionType<typeof pollingSuccess> | ActionType<typeof getDealSuccess>;

// TODO once we upgrade to typescript 4.1 add union type Action<`${string}/${"patch_vin" | "patch" | "put"}/request`>
type ClearActions = ActionType<typeof clearDealerChanges> | Action;

const dealerChanges: Reducer<
    DealerChangesState,
    UpdateActions | ClearActions | ActionType<typeof ancillaryProductUserSelections>
> = (state = initialState, action) => {
    if (isAncillaryProductUserSelectionsAction(action)) {
        return {
            ...state,
            ciqUserChanges: {
                ...state.ciqUserChanges,
                ancillaryProducts: { ...state.ciqUserChanges.ancillaryProducts, ...action.payload },
            },
        };
    }

    if (isUpdateAction(action)) {
        const filteredChanges = filterAncillaryProductChanges(
            action.payload.deal?.dealer_changes ?? [],
            state.ciqUserChanges.ancillaryProducts
        );

        return {
            ...state,
            added: [
                ...state.added,
                ...(filteredChanges
                    ?.filter(({ path }) => /^\/vehicles\/\w{17}$/.test(path))
                    .map(({ path }) => path.replace(/\/vehicles\//, "")) ?? []),
                ...Object.keys(filteredChanges?.find(({ path }) => /^\/vehicles$/.test(path))?.value ?? {}),
            ],
            changed: [
                ...state.changed,
                ...(filteredChanges
                    ?.filter(({ path }) => !/^\/vehicles\/\w{17}$/.test(path))
                    ?.filter(
                        ({ path }) =>
                            !path.includes("savings") && !path.includes("tracking") && !path.includes("person")
                    )
                    ?.map(({ path }) => path) ?? []),
            ],
        };
    }

    if (isClearAction(action)) {
        return {
            ...state,
            changed: [],
        };
    }

    return state;
};

export default dealerChanges;

function isAncillaryProductUserSelectionsAction(
    action: UpdateActions | ClearActions | ActionType<typeof ancillaryProductUserSelections>
): action is ActionType<typeof ancillaryProductUserSelections> {
    return action.type === ancillaryProductUserSelections.type;
}

function isUpdateAction(
    action: UpdateActions | ClearActions | ActionType<typeof ancillaryProductUserSelections>
): action is UpdateActions {
    return action.type === pollingSuccess.type || action.type === getDealSuccess.type;
}

function isClearAction(
    action: UpdateActions | ClearActions | ActionType<typeof ancillaryProductUserSelections>
): action is ClearActions {
    return action.type === clearDealerChanges.type || /\/patch|patch_vin|put\/request$/.test(action.type);
}

// Below are used for Filtering user added Ancillary Products out of the changes
// Needed because Credit IQ doesn't know the difference between a user and dealer change
function filterAncillaryProductChanges(
    dealerChanges: DealerChange[],
    ancillaryProducts: ActionType<typeof ancillaryProductUserSelections>["payload"] | undefined
): DealerChange[] {
    return dealerChanges
        .filter(ignoreAllAncillaryProductOpsExceptAdd)
        .filter((change) => ignoreAncillaryProductUserAdditions(change, ancillaryProducts));
}

function ignoreAllAncillaryProductOpsExceptAdd(change: DealerChange): boolean {
    if (change.path.includes("ancillary_products") && change.op !== "add") {
        return false;
    }
    return true;
}

function ignoreAncillaryProductUserAdditions(
    change: DealerChange,
    ancillaryProducts: ActionType<typeof ancillaryProductUserSelections>["payload"] | undefined
): boolean {
    if (!change.path.includes("ancillary_products")) {
        return true;
    }

    if (ancillaryProducts === undefined) {
        return true;
    }

    if (isAncillaryProductAddition(change)) {
        return !Object.entries(ancillaryProducts).some(([vin, products]) => {
            if (
                change.path.includes(vin) &&
                products.some((product) => {
                    return (
                        product.quoteId === change.value.quoteId &&
                        product.productId === change.value.id &&
                        product.optionId === change.value.options?.[0]?.id
                    );
                })
            ) {
                return true;
            }

            return false;
        });
    }

    return true;
}

function isAncillaryProductAddition(change: DealerChange): change is {
    value: AncillaryProduct;
    op: "add";
    path: string;
} {
    return change.path.includes("ancillary_products") && change.op === "add";
}
